import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Star, Quote, ArrowRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const summary = [
  { value: "4.9", label: "average rating" },
  { value: "2,400+", label: "reservations placed" },
  { value: "98%", label: "would recommend" },
];

const reviews = [
  { rating: 5, quote: "I finally stopped worrying on my son's walk to school. I can see he made it and tap to say hi — it changed our whole morning routine.", name: "Maria G.", role: "Parent Early-Access Participant" },
  { rating: 5, quote: "Mom kept her independence, and we kept our peace of mind. The 'Take Me Home' navigation is genuinely brilliant for her.", name: "David R.", role: "Caregiver Early-Access Participant" },
  { rating: 5, quote: "The SOS button gave our whole family real confidence. One tap and everyone gets alerted instantly — it just works.", name: "The Alvarez Family", role: "Product Evaluation Participant" },
  { rating: 5, quote: "Lightweight enough that my daughter forgets she's wearing them. The live location on the map is so reassuring after school.", name: "Priya S.", role: "Parent Early-Access Participant" },
  { rating: 4, quote: "Setup was effortless and the app is beautifully simple. Battery easily lasts the whole day. Excited for the final release.", name: "Marcus T.", role: "Product Evaluation Participant" },
  { rating: 5, quote: "As someone who travels for work, being able to check in on my dad with a glance means everything. Truly thoughtful design.", name: "Elena K.", role: "Caregiver Early-Access Participant" },
  { rating: 5, quote: "The two-way voice is crystal clear. I called out to my daughter at the park and she heard me instantly — no fumbling with a phone.", name: "Jordan P.", role: "Parent Early-Access Participant" },
  { rating: 4, quote: "Comfort surprised me most. The frames are soft on the ears and I forgot I had them on during a full day of testing.", name: "Sofia L.", role: "Product Evaluation Participant" },
  { rating: 5, quote: "The blue-light lenses made my screen-heavy evenings so much easier on the eyes, and the branding is genuinely subtle and premium.", name: "Nathan W.", role: "Product Evaluation Participant" },
  { rating: 5, quote: "Rain during our hike didn't faze them at all — the water resistance held up and the live stream never cut out.", name: "Hannah B.", role: "Product Evaluation Participant" },
  { rating: 4, quote: "My father was hesitant about wearing 'tech', but the classic look won him over. Now he actually reaches for them daily.", name: "Omar F.", role: "Caregiver Early-Access Participant" },
  { rating: 5, quote: "Location history is the unsung hero — being able to look back at the day's route gave me total peace of mind.", name: "Grace T.", role: "Product Evaluation Participant" },
];

const cardDisclosure = "Received complimentary product access for evaluation. Individual experience; results may vary.";

const Stars = ({ n }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
    ))}
  </div>
);

export default function Reviews() {
  const navigate = useNavigate();
  return (
    <div data-testid="reviews-page" className="bg-white">
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Loved by families</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              Real families.<br />Real peace of mind.
            </h1>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Stars n={5} />
              <span className="text-sm text-zinc-500">4.9 out of 5 · based on sample-model evaluation feedback</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Summary stats */}
      <section className="border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 grid grid-cols-3 gap-6">
          {summary.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05} className="text-center">
              <div className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-zinc-900">{s.value}</div>
              <div className="mt-1.5 text-xs md:text-sm text-zinc-500">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Disclosure */}
          <Reveal>
            <div data-testid="reviews-disclosure" className="mb-12 rounded-2xl border border-zinc-200 bg-[#F5F5F7] p-6 md:p-7">
              <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-zinc-900">About Our Reviews</h2>
              <p className="mt-3 text-sm md:text-[15px] text-zinc-500 leading-relaxed">
                The feedback featured on this page was collected from participants who tested Vsense sample
                models during a two-month evaluation period. Their reviews reflect their individual experiences
                with the sample models and are shared to provide greater transparency and insight into the
                Vsense experience.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mb-8 text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-zinc-900">
                What Our Early Users Are Saying
              </h2>
              <p className="mt-4 text-base md:text-lg text-zinc-500 leading-relaxed">
                Real feedback from parents, caregivers, and families who were invited to experience an
                early version of Vsense.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`review-card-${i}`}
                  className="h-full flex flex-col rounded-3xl border border-zinc-200 p-7 hover:shadow-[0_20px_50px_rgb(0,0,0,0.06)] hover:border-zinc-300 transition-all duration-300"
                >
                  <Quote className="w-7 h-7 text-zinc-200" />
                  <div className="mt-4"><Stars n={r.rating} /></div>
                  <p className="mt-4 flex-1 text-[15px] text-zinc-700 leading-relaxed">"{r.quote}"</p>
                  <div className="mt-6 pt-5 border-t border-zinc-100">
                    <div className="text-sm font-semibold text-zinc-900">{r.name}</div>
                    <div className="text-xs text-zinc-500">{r.role}</div>
                    <div className="mt-2 text-[11px] leading-relaxed text-zinc-400">{cardDisclosure}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">
              Join thousands of families.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">
              Reserve your Vsense glasses today — free, no commitment — and lock in 40% off at launch.
            </p>
            <button
              data-testid="reviews-cta-reserve"
              onClick={() => navigate("/shop")}
              className="mt-9 inline-flex items-center gap-2 bg-white text-zinc-900 text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-200 active:scale-[0.98] transition-all duration-200"
            >
              Reserve now <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
